import { useState, useEffect, useRef } from 'react';
import { trpc } from '../lib/trpc';
import { useProject } from '../context';

/**
 * Type for media category configuration.
 */
export interface MediaCategory {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  order: number;
}

interface ManageCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

/**
 * Modal component for managing media categories.
 *
 * Allows admins to add, edit, delete, and reorder media categories
 * that are used to organize media assets in the Media Library.
 * Changes are persisted via tRPC to the bot configuration.
 */
export function ManageCategoriesModal({
  isOpen,
  onClose,
  onSave,
}: ManageCategoriesModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const { currentBotId: botId } = useProject();
  const utils = trpc.useUtils();

  // Local state for categories
  const [categories, setCategories] = useState<MediaCategory[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load bot data
  const botQuery = trpc.bots.get.useQuery(
    { id: botId! },
    { enabled: !!botId && isOpen }
  );

  // Update mutation
  const updateMutation = trpc.bots.update.useMutation({
    onSuccess: () => {
      utils.bots.get.invalidate({ id: botId! });
      utils.bots.list.invalidate();
      setHasChanges(false);
      setSaveError(null);
      onSave?.();
      onClose();
    },
    onError: (error) => {
      setSaveError(error.message);
    },
  });

  // Initialize categories from bot data
  useEffect(() => {
    if (botQuery.data && isOpen && !isInitialized) {
      const mediaCategories =
        botQuery.data.mediaCategories &&
        Array.isArray(botQuery.data.mediaCategories)
          ? (botQuery.data.mediaCategories as MediaCategory[])
          : [];
      setCategories(mediaCategories);
      setIsInitialized(true);
      setHasChanges(false);
      setSaveError(null);
    }
  }, [botQuery.data, isOpen, isInitialized]);

  // Reset initialization when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  /**
   * Generate a unique ID for new media categories.
   */
  const generateCategoryId = () => {
    return `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  /**
   * Add a new media category.
   */
  const handleAddCategory = () => {
    const newCategory: MediaCategory = {
      id: generateCategoryId(),
      name: '',
      order: categories.length,
    };
    setCategories((prev) => [...prev, newCategory]);
    setHasChanges(true);
  };

  /**
   * Remove a media category.
   */
  const handleRemoveCategory = (index: number) => {
    setCategories((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  /**
   * Update a media category field.
   */
  const handleUpdateCategory = (
    index: number,
    field: keyof MediaCategory,
    value: string | number
  ) => {
    setCategories((prev) => {
      const newCategories = [...prev];
      newCategories[index] = { ...newCategories[index], [field]: value };
      return newCategories;
    });
    setHasChanges(true);
  };

  /**
   * Move a category up in the list.
   */
  const handleMoveCategoryUp = (index: number) => {
    if (index === 0) return;
    setCategories((prev) => {
      const newCategories = [...prev];
      [newCategories[index - 1], newCategories[index]] = [newCategories[index], newCategories[index - 1]];
      // Update order values
      newCategories.forEach((cat, i) => { cat.order = i; });
      return newCategories;
    });
    setHasChanges(true);
  };

  /**
   * Move a category down in the list.
   */
  const handleMoveCategoryDown = (index: number) => {
    if (index === categories.length - 1) return;
    setCategories((prev) => {
      const newCategories = [...prev];
      [newCategories[index], newCategories[index + 1]] = [newCategories[index + 1], newCategories[index]];
      // Update order values
      newCategories.forEach((cat, i) => { cat.order = i; });
      return newCategories;
    });
    setHasChanges(true);
  };

  /**
   * Handle saving categories to the backend.
   */
  const handleSave = () => {
    if (!botId || !hasChanges || updateMutation.isPending) return;

    setSaveError(null);

    // Filter out categories without names
    const validCategories = categories.filter(cat => cat.name.trim());

    updateMutation.mutate({
      id: botId,
      mediaCategories: validCategories.length > 0 ? validCategories : null,
    });
  };

  if (!isOpen) return null;

  // Handle no bot selected
  if (!botId) {
    return (
      <div style={styles.overlay}>
        <div ref={modalRef} style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>Manage Categories</h2>
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close modal"
            >
              &times;
            </button>
          </div>
          <div style={styles.content}>
            <div style={styles.errorMessage}>
              No bot selected. Please select a bot from the sidebar.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (botQuery.isLoading) {
    return (
      <div style={styles.overlay}>
        <div ref={modalRef} style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>Manage Categories</h2>
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close modal"
            >
              &times;
            </button>
          </div>
          <div style={styles.content}>
            <div style={styles.loadingState}>Loading categories...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Manage Categories</h2>
          <button
            onClick={onClose}
            style={styles.closeButton}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div style={styles.content}>
          <p style={styles.description}>
            Define custom categories to organize media assets in the Media Library.
            These categories allow you to filter and assign media to specific groups.
          </p>

          {saveError && (
            <div style={styles.errorMessage}>{saveError}</div>
          )}

          {/* Categories List */}
          <div style={styles.categoriesContainer}>
            {categories.map((category, index) => (
              <div key={category.id} style={styles.categoryCard}>
                <div style={styles.categoryCardHeader}>
                  <span style={styles.categoryNumber}>{index + 1}</span>
                  <input
                    type="text"
                    value={category.name}
                    onChange={(e) => handleUpdateCategory(index, 'name', e.target.value)}
                    placeholder="Category name"
                    style={styles.categoryNameInput}
                    maxLength={50}
                  />
                  <div style={styles.categoryActions}>
                    <button
                      type="button"
                      onClick={() => handleMoveCategoryUp(index)}
                      disabled={index === 0}
                      style={{
                        ...styles.categoryActionButton,
                        ...(index === 0 ? styles.categoryActionButtonDisabled : {}),
                      }}
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveCategoryDown(index)}
                      disabled={index === categories.length - 1}
                      style={{
                        ...styles.categoryActionButton,
                        ...(index === categories.length - 1
                          ? styles.categoryActionButtonDisabled
                          : {}),
                      }}
                      title="Move Down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveCategory(index)}
                      style={styles.categoryRemoveButton}
                      title="Remove Category"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <input
                  type="text"
                  value={category.description || ''}
                  onChange={(e) => handleUpdateCategory(index, 'description', e.target.value)}
                  placeholder="Optional description"
                  style={styles.categoryDescriptionInput}
                  maxLength={200}
                />
              </div>
            ))}
          </div>

          {categories.length === 0 && (
            <div style={styles.emptyState}>
              No categories defined yet. Click the button below to add your first category.
            </div>
          )}

          <button
            type="button"
            onClick={handleAddCategory}
            style={styles.addCategoryButton}
          >
            + Add Category
          </button>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button
            onClick={onClose}
            style={styles.cancelButton}
            disabled={updateMutation.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              ...styles.saveButton,
              ...((!hasChanges || updateMutation.isPending) ? styles.saveButtonDisabled : {}),
            }}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    color: '#64748b',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  content: {
    padding: '20px',
    overflowY: 'auto',
    flex: 1,
  },
  description: {
    fontSize: '14px',
    color: '#64748b',
    marginBottom: '20px',
    lineHeight: '1.5',
    margin: '0 0 20px 0',
  },
  loadingState: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  errorMessage: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '14px',
    marginBottom: '16px',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#64748b',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px dashed #e2e8f0',
    marginBottom: '16px',
  },
  categoriesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '16px',
  },
  categoryCard: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    borderLeft: '4px solid #8B5CF6',
  },
  categoryCardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  categoryNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#8B5CF6',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  categoryNameInput: {
    flex: 1,
    minWidth: '150px',
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    fontFamily: 'inherit',
    backgroundColor: '#ffffff',
  },
  categoryDescriptionInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
  },
  categoryActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: 'auto',
  },
  categoryActionButton: {
    padding: '4px 8px',
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#475569',
  },
  categoryActionButtonDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  categoryRemoveButton: {
    padding: '4px 10px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#dc2626',
  },
  addCategoryButton: {
    padding: '10px 20px',
    backgroundColor: '#f5f3ff',
    border: '1px solid #c4b5fd',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#7C3AED',
    width: '100%',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  saveButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
};

export default ManageCategoriesModal;
